import React from 'react';
import { createPortal } from 'react-dom';

import { Text } from '@/nova/components';

import * as S from './BannerKitComposer.styles';

/** The gap between the badge and its tooltip, and the tooltip's distance from the screen's edges. */
const GAP_PX = 8;

export interface ReviewBadgeTipProps {
  /** A stable id for the tooltip (it is the badge's description). */
  id: string;
  /** The reason, already translated. */
  reason: string;
}

/**
 * "Needs review" with its reason in a tooltip on hover or keyboard focus. The tooltip is drawn on the page's top layer
 * (a portal, fixed to the screen), placed above the badge (below when there is no room), kept inside the screen and
 * moved with any scroll: drawn inside the card it was covered by the neighbouring cards and widened the grid.
 */
export const ReviewBadgeTip = ({ id, reason }: ReviewBadgeTipProps): React.ReactElement => {
  const badgeRef = React.useRef<HTMLSpanElement>(null);
  const tipRef = React.useRef<HTMLSpanElement>(null);
  const [open, setOpen] = React.useState(false);
  const [pos, setPos] = React.useState<{ top: number; left: number } | null>(null);

  // Syncs with the DOM: where the badge is now (on open, on any scroll, on resize).
  React.useLayoutEffect(() => {
    if (!open) {
      setPos(null);
      return undefined;
    }
    const place = (): void => {
      const badge = badgeRef.current;
      const tip = tipRef.current;
      if (!badge || !tip) return;
      const b = badge.getBoundingClientRect();
      const w = tip.offsetWidth;
      const h = tip.offsetHeight;
      const left = Math.min(Math.max(GAP_PX, b.left), window.innerWidth - w - GAP_PX);
      const above = b.top - GAP_PX - h;
      setPos({ top: above >= GAP_PX ? above : b.bottom + GAP_PX, left });
    };
    place();
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    return () => {
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
    };
  }, [open]);

  return (
    <>
      <S.ReviewTip
        ref={badgeRef}
        // Focusable so a keyboard user gets the reason too; the tooltip is its description.
        // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
        tabIndex={0}
        aria-describedby={id}
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
      >
        <S.ReviewBadge text="Needs review" />
      </S.ReviewTip>
      {open
        ? createPortal(
            <S.ReviewTipText
              ref={tipRef}
              id={id}
              role="tooltip"
              $shown={pos !== null}
              style={{ top: pos?.top ?? 0, left: pos?.left ?? 0 }}
            >
              <Text tag="span" raw text={reason} />
            </S.ReviewTipText>,
            document.body,
          )
        : null}
    </>
  );
};

export default ReviewBadgeTip;
